import { Filter, Search, Plus, Wand2, Eraser, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import { SpaceSwitcher } from '../../common/SpaceSwitcher';
import { StatusFilterDropdown } from './gallery/StatusFilterDropdown';
import type { PostStatusFilter } from '../../../utils/Constants';
import { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useAuth } from '../../../hooks/useAuth';
import type { CommunicationChannel, Post } from '../../../types';
import { defaultChannels } from '../../../config/channels';
import { motion, AnimatePresence } from 'motion/react';
import { MobileGeneratePanel } from '../generation/MobileGeneratePanel';
import { MobileCreatePanel } from '../generation/MobileCreatePanel';
import { channelIcons, ChannelSvgIcon } from '../../../utils/Constants';

interface MobilePostGalleryHeaderProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  sortType: 'date' | 'channel' | 'alphabetical';
  sortOrder: 'asc' | 'desc';
  onSortTypeChange: (type: 'date' | 'channel' | 'alphabetical') => void;
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  /** UX backlog : filtre de statut maintenant exposé dans le mobile header. */
  statusFilter?: PostStatusFilter;
  onStatusFilterChange?: (status: PostStatusFilter) => void;
  // V4-MOBILE-BOTTOMBAR-DEAD-PROP-ONOPENFILTERS 2026-07-30 — `onOpenFilters` a été
  // retiré : il était déclaré obligatoire ici mais jamais déstructuré ni appelé, et
  // `MobileBottomBar` lui passait un `() => {}`. Les filtres s'ouvrent en réalité via
  // le tiroir interne `showFilters` (bouton entonnoir ci-dessous), pas par un callback
  // remontant au parent. Ne pas le réintroduire sans un consommateur réel.
  onClearGallery: () => void;
  // Filter props
  availableChannels?: CommunicationChannel[];
  selectedChannelIds?: Set<string>;
  onToggleChannel?: (channelId: string) => void;
  showExternal?: boolean;
  showInternal?: boolean;
  onToggleExternal?: () => void;
  onToggleInternal?: () => void;
  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — filtre « mes posts »
  showOnlyMine?: boolean;
  onToggleOnlyMine?: () => void;
  canFilterMine?: boolean;
  startDate?: string;
  endDate?: string;
  onStartDateChange?: (date: string) => void;
  onEndDateChange?: (date: string) => void;
  // Generate props
  communicationChannels?: CommunicationChannel[];
  onGenerateComplete?: (posts: Post[]) => void;
  // Create props
  onCreateComplete?: (post: Post) => void;
}

export function MobilePostGalleryHeader({
  searchQuery,
  onSearchChange,
  sortType,
  sortOrder,
  onSortTypeChange,
  onSortOrderChange,
  statusFilter,
  onStatusFilterChange,
  onClearGallery,
  // Filter props
  availableChannels,
  selectedChannelIds,
  onToggleChannel,
  showExternal,
  showInternal,
  onToggleExternal,
  onToggleInternal,
  showOnlyMine,
  onToggleOnlyMine,
  canFilterMine,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  // Generate props
  communicationChannels,
  onGenerateComplete,
  // Create props
  onCreateComplete,
}: MobilePostGalleryHeaderProps) {
  const [showSearch, setShowSearch] = useState(false);
  const [showSort, setShowSort] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showGenerate, setShowGenerate] = useState(false);
  const { t } = useLanguage();
  // currentSpace : nom de l'espace courant dans le placeholder de recherche (V4-SPACES-SEARCH-CONTEXT).
  const { currentSpace } = useAuth();

  if (!t || !t.posts) return null;

  const getChannelIcon = (channel: CommunicationChannel): string => {
    return channelIcons[channel.network] || (channel.network || '?').charAt(0);
  };

  const getChannelLabel = (channel: CommunicationChannel): string => {
    if (channel.customName) {
      return channel.customName;
    }
    const config = defaultChannels.find(c => c.name === channel.network);
    return (config?.translationKey && t.networks ? t.networks[config.translationKey as keyof typeof t.networks] : config?.displayName) || channel.network;
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Main toolbar - pill shape with icons - ALWAYS rounded-full */}
      <div 
        className="rounded-full shadow-lg"
        style={{ 
          backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          border: '1px solid var(--color-border)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* V4-MOBILE-HEADER-TOUCH-TARGETS 2026-07-30 — les contrôles étaient écrasés à
            ~20 px de large : `w-10` (40 px) n'est qu'une largeur *souhaitée*, et flexbox
            rétrécissait tout le monde. Chaque enfant est donc `flexShrink: 0` avec un
            minimum explicite de 40 px (cible tactile), et chacun porte un `aria-label`
            + `title` — ils étaient tous en icône seule sans nom accessible, dont
            « vider la galerie » qui est destructif.

            V4-MOBILE-HEADER-STATUS-CROPPED 2026-07-30 — le pill ne peut PAS contenir
            9 contrôles : 8 × 40 px + 7 espaces = 348 px > 343 px utiles (375 px moins
            `px-4`). La 1ʳᵉ version rendait donc la rangée défilante, mais taper
            l'entonnoir déclenchait le scroll-into-view du navigateur sur l'élément
            focalisé, ce qui rognait le contrôle de statut à gauche. Le défilement
            traitait le symptôme.
            Correctif : le filtre de statut (106 px, seul contrôle *libellé*) descend
            dans le tiroir de filtres, où il a la place d'afficher son libellé. Le pill
            garde 6 boutons de 40 px + le sélecteur d'espace. Budget mesuré au DOM :
            la rangée offre 309 px de contenu (341 px de `clientWidth` moins son `px-4`),
            et 6 × 40 + 36 (sélecteur) + 1 (séparateur) + 7 × 4 (`gap-1`) = 305 px.
            Le séparateur perd son `mx-1` : il coûtait 8 px de marges, ce qui suffisait
            à faire déborder de 18 px (vérifié).
            `overflowX` reste en filet de sécurité (écrans < 375 px, nom d'espace long)
            mais ne doit plus se déclencher dans le cas nominal. En style inline et pas
            en classe : `overflow-x-auto` est absent du CSS Tailwind figé
            (cf. AGENTS.md V4-FRONT-TAILWIND-FROZEN-CSS). */}
        <div
          className="px-4 py-3 flex items-center justify-between gap-1"
          style={{ overflowX: 'auto', scrollbarWidth: 'none' }}
        >
          {/* Search */}
          <button
            type="button"
            aria-label={t.posts.search}
            title={t.posts.search}
            aria-pressed={showSearch}
            onClick={() => {
              setShowSearch(!showSearch);
              setShowSort(false);
              setShowFilters(false);
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
            style={{
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
              ...(showSearch ? { backgroundColor: 'var(--color-primary)' } : {}),
            }}
          >
            <Search
              className="w-5 h-5"
              style={{ color: showSearch ? 'var(--color-button-text)' : 'var(--color-text-muted)' }}
              stroke={showSearch ? 'var(--color-button-text)' : 'var(--color-text-muted)'}
            />
          </button>

          {/* Sort */}
          <button
            type="button"
            aria-label={t.posts.sortBy}
            title={t.posts.sortBy}
            aria-pressed={showSort}
            onClick={() => {
              setShowSort(!showSort);
              setShowSearch(false);
              setShowFilters(false);
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
            style={{
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
              ...(showSort ? { backgroundColor: 'var(--color-primary)' } : {}),
            }}
          >
            <ArrowUpDown
              className="w-5 h-5"
              style={{ color: showSort ? 'var(--color-button-text)' : 'var(--color-text-muted)' }}
              stroke={showSort ? 'var(--color-button-text)' : 'var(--color-text-muted)'}
            />
          </button>

          {/* Filters */}
          <button
            type="button"
            aria-label={t.posts.filters}
            title={t.posts.filters}
            aria-pressed={showFilters}
            onClick={() => {
              setShowFilters(!showFilters);
              setShowSearch(false);
              setShowSort(false);
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all"
            style={{
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
              ...(showFilters ? { backgroundColor: 'var(--color-primary)' } : {}),
            }}
          >
            <Filter
              className="w-5 h-5"
              style={{ color: showFilters ? 'var(--color-button-text)' : 'var(--color-text-muted)' }}
              stroke={showFilters ? 'var(--color-button-text)' : 'var(--color-text-muted)'}
            />
          </button>

          {/* Space switcher — visible uniquement si plusieurs spaces (comptes agences) */}
          <div style={{ flexShrink: 0 }}>
            <SpaceSwitcher />
          </div>

          {/* Divider — sans `mx-1` : ses 8 px de marges faisaient déborder la rangée. */}
          <div
            className="w-px h-6"
            style={{ backgroundColor: 'var(--color-border)', flexShrink: 0 }}
          />

          {/* Create Post */}
          <button
            type="button"
            aria-label={t.posts.createPost}
            title={t.posts.createPost}
            aria-pressed={showCreate}
            onClick={() => {
              setShowCreate(!showCreate);
              setShowGenerate(false);
              setShowSearch(false);
              setShowSort(false);
              setShowFilters(false);
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-opacity-10"
            style={{
              color: 'var(--color-text-muted)',
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
            }}
          >
            <Plus className="w-5 h-5" />
          </button>

          {/* Generate Posts */}
          {/* V4-ONBOARDING-TOUR 2026-06-18 — cible du tour produit (mobile). */}
          <button
            type="button"
            data-tour="generate"
            aria-label={t.posts.generatePosts}
            title={t.posts.generatePosts}
            aria-pressed={showGenerate}
            onClick={() => {
              setShowGenerate(!showGenerate);
              setShowCreate(false);
              setShowSearch(false);
              setShowSort(false);
              setShowFilters(false);
            }}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-opacity-10"
            style={{
              color: 'var(--color-text-muted)',
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
            }}
          >
            <Wand2 className="w-5 h-5" />
          </button>

          {/* Clear Gallery — action DESTRUCTIVE, le nom accessible n'est pas optionnel. */}
          <button
            type="button"
            aria-label={t.confirm.clearGallery}
            title={t.confirm.clearGallery}
            onClick={onClearGallery}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:bg-opacity-10"
            style={{
              color: 'var(--color-text-muted)',
              flexShrink: 0,
              minWidth: '40px',
              minHeight: '40px',
            }}
          >
            <Eraser className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Expandable sections - separate glassmorphism containers below */}
      <AnimatePresence>
        {/* Search section */}
        {showSearch && (
          <motion.div
            key="search-section"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="rounded-3xl shadow-lg"
            style={{ 
              backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid var(--color-border)`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              overflow: 'visible',
            }}
          >
            <div className="p-4">
              <input
                type="text"
                placeholder={currentSpace?.name
                  ? t.posts.searchPlaceholderInSpace.replace('{space}', currentSpace.name)
                  : t.posts.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                autoFocus
                className="w-full rounded-full px-4 py-2 border"
                style={{
                  borderColor: 'var(--color-border)',
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-primary)',
                  outline: 'none'
                }}
              />
            </div>
          </motion.div>
        )}

        {/* Sort section */}
        {showSort && (
          <motion.div
            key="sort-section"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-3xl shadow-lg"
            style={{ 
              backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid var(--color-border)`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="p-4 space-y-3">
              {/* Sort type - 3 boutons l'un sous l'autre */}
              <div className="space-y-2">
                <button
                  onClick={() => onSortTypeChange('date')}
                  className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-between"
                  style={sortType === 'date' ? {
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-button-text)'
                  } : {
                    backgroundColor: 'transparent',
                    border: `1px solid var(--color-border)`,
                    color: 'var(--color-text-muted)'
                  }}
                >
                  <span>{t.posts.sortByDate}</span>
                  {sortType === 'date' && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                      className="ml-2 cursor-pointer"
                      role="button"
                      aria-label={sortOrder === 'asc' ? t.posts.sortAscending : t.posts.sortDescending}
                    >
                      {sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      ) : (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      )}
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => onSortTypeChange('channel')}
                  className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-between"
                  style={sortType === 'channel' ? {
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-button-text)'
                  } : {
                    backgroundColor: 'transparent',
                    border: `1px solid var(--color-border)`,
                    color: 'var(--color-text-muted)'
                  }}
                >
                  <span>{t.posts.sortByChannel}</span>
                  {sortType === 'channel' && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                      className="ml-2 cursor-pointer"
                      role="button"
                      aria-label={sortOrder === 'asc' ? t.posts.sortAscending : t.posts.sortDescending}
                    >
                      {sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      ) : (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      )}
                    </div>
                  )}
                </button>
                
                <button
                  onClick={() => onSortTypeChange('alphabetical')}
                  className="w-full py-2 px-3 rounded-full text-sm transition-all flex items-center justify-between"
                  style={sortType === 'alphabetical' ? {
                    backgroundColor: 'var(--color-primary)',
                    color: 'var(--color-button-text)'
                  } : {
                    backgroundColor: 'transparent',
                    border: `1px solid var(--color-border)`,
                    color: 'var(--color-text-muted)'
                  }}
                >
                  <span>A / Z</span>
                  {sortType === 'alphabetical' && (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        onSortOrderChange(sortOrder === 'asc' ? 'desc' : 'asc');
                      }}
                      className="ml-2 cursor-pointer"
                      role="button"
                      aria-label={sortOrder === 'asc' ? t.posts.sortAscending : t.posts.sortDescending}
                    >
                      {sortOrder === 'asc' ? (
                        <ChevronUp className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      ) : (
                        <ChevronDown className="w-4 h-4" style={{ color: 'var(--color-button-text)' }} />
                      )}
                    </div>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Filters section */}
        {showFilters && (
          <motion.div
            key="filters-section"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-3xl shadow-lg"
            style={{ 
              backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid var(--color-border)`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="p-4 space-y-4">
              {/* V4-MOBILE-HEADER-STATUS-CROPPED 2026-07-30 — le filtre de statut vivait
                  dans le pill, où ses 106 px faisaient déborder la rangée (donc défiler,
                  donc rogner le libellé). Sa place est ici : le tiroir a la largeur pour
                  l'afficher en entier, et le statut courant reste lisible d'un coup d'œil.
                  `t.posts.statusFilter` est la clé déjà utilisée par le dropdown lui-même. */}
              {statusFilter !== undefined && onStatusFilterChange && (
                <div className="space-y-2">
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                    {t.posts.statusFilter}
                  </span>
                  <StatusFilterDropdown value={statusFilter} onChange={onStatusFilterChange} />
                </div>
              )}

              {/* External/Internal filters */}
              {/* V4-MOBILE-HEADER-TOUCH-TARGETS 2026-07-30 — ces 3 bascules étaient des
                  pastilles de 16 px sans nom accessible, le libellé vivant dans un
                  `<span>` voisin non relié. Le bouton englobe maintenant pastille +
                  libellé (rangée de 44 px), donc la cible tactile ET le nom accessible
                  couvrent le texte visible. */}
              {onToggleExternal && onToggleInternal && (
                <div className="space-y-3">
                  {[
                    { key: 'external', label: t.posts.external, active: !!showExternal, onToggle: onToggleExternal },
                    { key: 'internal', label: t.posts.internal, active: !!showInternal, onToggle: onToggleInternal },
                    ...(canFilterMine && onToggleOnlyMine
                      // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — « mes posts »
                      ? [{ key: 'onlyMine', label: t.posts.onlyMine, active: !!showOnlyMine, onToggle: onToggleOnlyMine }]
                      : []),
                  ].map(({ key, label, active, onToggle }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={onToggle}
                      aria-pressed={active}
                      className="w-full flex items-center gap-2 text-left"
                      style={{ minHeight: '44px' }}
                    >
                      <span
                        aria-hidden="true"
                        className={`w-4 h-4 rounded-full transition-all ${active ? '' : 'border-2 bg-transparent'}`}
                        style={{
                          flexShrink: 0,
                          ...(active
                            ? {
                                backgroundColor: 'var(--color-primary)',
                                boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)',
                              }
                            : { borderColor: 'var(--color-accent)' }),
                        }}
                      />
                      <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>
                        {label}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Channel filters - picto + nom comme desktop */}
              {availableChannels && availableChannels.length > 0 && onToggleChannel && selectedChannelIds && (
                <div className="space-y-2">
                  {availableChannels.map((channel) => {
                    const networkConfig = defaultChannels.find(c => c.name === channel.network);
                    const isActive = selectedChannelIds.has(channel.id);
                    
                    return (
                      <button
                        key={channel.id}
                        onClick={() => onToggleChannel(channel.id)}
                        className="w-full flex items-center gap-2 transition-opacity hover:opacity-80"
                      >
                        <ChannelSvgIcon
                          network={channel.network}
                          size={28}
                          fallbackClassName={isActive ? `${networkConfig?.color} ${networkConfig?.textColor}` : 'border'}
                          fallbackStyle={!isActive ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)', opacity: 0.7 } : undefined}
                        />
                        <span 
                          className="text-sm font-medium truncate"
                          style={{ 
                            color: isActive ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                            opacity: isActive ? 1 : 0.7
                          }}
                        >
                          {getChannelLabel(channel)}
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Date filters - affichés directement comme sur desktop */}
              {onStartDateChange && onEndDateChange && (
                <div 
                  className="pt-4 space-y-3" 
                  style={{ 
                    borderTop: `1px solid var(--color-border)` 
                  }}
                >
                  {/* Mode toggle - Date ou Période */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        // Mode date unique - clear end date
                        onEndDateChange('');
                      }}
                      className="flex-1 py-2 px-3 rounded-full text-sm transition-all"
                      style={!endDate ? {
                        backgroundColor: 'var(--color-primary)',
                        color: 'var(--color-button-text)'
                      } : {
                        backgroundColor: 'transparent',
                        border: `1px solid var(--color-border)`,
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      {t.posts.date}
                    </button>
                    <button
                      onClick={() => {
                        // Mode période - set a dummy end date to activate period mode
                        if (!endDate) {
                          // Use start date as end date to activate period mode
                          onEndDateChange(startDate || '');
                        }
                      }}
                      className="flex-1 py-2 px-3 rounded-full text-sm transition-all"
                      style={endDate ? {
                        backgroundColor: 'var(--color-primary)',
                        color: 'var(--color-button-text)'
                      } : {
                        backgroundColor: 'transparent',
                        border: `1px solid var(--color-border)`,
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      {t.posts.period}
                    </button>
                  </div>

                  {/* Date inputs */}
                  {!endDate ? (
                    // Mode date unique
                    <div className="space-y-2">
                      <label className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                        {t.posts.date}
                      </label>
                      <input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => onStartDateChange(e.target.value)}
                        className="w-full rounded-full text-sm px-4 py-2 border"
                        style={{
                          borderColor: 'var(--color-border)',
                          backgroundColor: 'var(--color-background)',
                          color: 'var(--color-text-primary)'
                        }}
                      />
                    </div>
                  ) : (
                    // Mode période
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <label className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {t.posts.startDate}
                        </label>
                        <input
                          type="date"
                          value={startDate || ''}
                          onChange={(e) => onStartDateChange(e.target.value)}
                          className="w-full rounded-full text-sm px-4 py-2 border"
                          style={{
                            borderColor: 'var(--color-border)',
                            backgroundColor: 'var(--color-background)',
                            color: 'var(--color-text-primary)'
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {t.posts.endDate}
                        </label>
                        <input
                          type="date"
                          value={endDate || ''}
                          onChange={(e) => onEndDateChange(e.target.value)}
                          className="w-full rounded-full text-sm px-4 py-2 border"
                          style={{
                            borderColor: 'var(--color-border)',
                            backgroundColor: 'var(--color-background)',
                            color: 'var(--color-text-primary)'
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Clear button */}
                  {(startDate || endDate) && (
                    <button
                      onClick={() => {
                        onStartDateChange('');
                        onEndDateChange('');
                      }}
                      className="w-full py-2 px-3 rounded-full text-sm transition-all"
                      style={{
                        backgroundColor: 'transparent',
                        border: `1px solid var(--color-border)`,
                        color: 'var(--color-text-muted)'
                      }}
                    >
                      {t.posts.clear}
                    </button>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Create Post section */}
        {showCreate && (
          <motion.div
            key="create-section"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-3xl shadow-lg"
            style={{ 
              backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid var(--color-border)`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="p-4">
              {communicationChannels && onCreateComplete && (
                <MobileCreatePanel
                  communicationChannels={communicationChannels}
                  onCreate={onCreateComplete}
                  onClose={() => setShowCreate(false)}
                />
              )}
            </div>
          </motion.div>
        )}

        {/* Generate Posts section */}
        {showGenerate && (
          <motion.div
            key="generate-section"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden rounded-3xl shadow-lg"
            style={{ 
              backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: `1px solid var(--color-border)`,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div className="p-4">
              {communicationChannels && onGenerateComplete && (
                <MobileGeneratePanel
                  communicationChannels={communicationChannels}
                  onGenerate={onGenerateComplete}
                  onClose={() => setShowGenerate(false)}
                />
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}